import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  CircularProgress,
  Collapse,
  Container,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { observer, useLocalObservable } from "mobx-react";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import ConfirmationPopup from "../../../../../views/components/common/pop-up/ConfirmationPopup";
import CatalogProductTable from "./CatalogProductTable";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import { useStores } from "../../../use-stores";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { CatalogProductStore } from "../../../stores/CatalogProductStore";
import { CategoryStore } from "../../../stores/CategoryStore";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import {
  PRODUCT_SORT_BY_OPTIONS,
  ProductListApi,
  ProductListFilters,
  getApiErrorMessage,
  normalizeProductListSortBy,
} from "../../../../../models/CatalogProducts/types";
import { SortType } from "../../../../../models/SortType";

const SEARCH_DEBOUNCE_MS = 500;
const DEFAULT_LIMIT = 30;

const SORT_BY_LIST: FilterValue[] = PRODUCT_SORT_BY_OPTIONS.map((o) => ({
  id: o.value,
  value: o.label,
}));

const SORT_DIRECTION_LIST: FilterValue[] = [
  { id: SortType.ASC, value: "Ascending" },
  { id: SortType.DESC, value: "Descending" },
];

const ACTIVE_FILTER_LIST: FilterValue[] = [
  { id: "all", value: "All" },
  { id: "1", value: "Active" },
  { id: "0", value: "Inactive" },
];

const DISPLAY_FILTER_LIST: FilterValue[] = [
  { id: "all", value: "All" },
  { id: "1", value: "Displayed" },
  { id: "0", value: "Hidden" },
];

function parseSortDirectionFromSearch(searchParams: URLSearchParams): SortType {
  const dirRaw =
    searchParams.get("sort_direction") ||
    searchParams.get("sort_order") ||
    searchParams.get("sort_type");
  const dirNorm = dirRaw?.trim().toUpperCase();
  if (!dirNorm) return SortType.DESC;
  return dirNorm === "ASC" ? SortType.ASC : SortType.DESC;
}

function parseFlagFilter(
  searchParams: URLSearchParams,
  key: string,
): 0 | 1 | undefined {
  const v = searchParams.get(key);
  if (v === "1" || v === "true") return 1;
  if (v === "0" || v === "false") return 0;
  return undefined;
}

function parseOptionalNonNegativeNumber(
  raw: string | null | undefined,
): number | undefined {
  if (raw == null || String(raw).trim() === "") return undefined;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return n;
}

function parseCategoryIdsFromSearch(
  raw: string | null | undefined,
): number[] | undefined {
  if (raw == null || String(raw).trim() === "") return undefined;
  const ids = String(raw)
    .split(",")
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => Number.isFinite(n) && n > 0);
  return ids.length ? ids : undefined;
}

function sameCategoryIds(a?: number[], b?: number[]): boolean {
  const left = a ?? [];
  const right = b ?? [];
  if (left.length !== right.length) return false;
  const sortedLeft = [...left].sort((x, y) => x - y);
  const sortedRight = [...right].sort((x, y) => x - y);
  return sortedLeft.every((id, index) => id === sortedRight[index]);
}

function getParamsFromSearch(search: string): ProductListFilters {
  const searchParams = new URLSearchParams(search);
  const pageRaw = searchParams.get("page");
  const limitRaw = searchParams.get("limit");
  const minSellingPrice = parseOptionalNonNegativeNumber(
    searchParams.get("min_selling_price"),
  );
  const maxSellingPrice = parseOptionalNonNegativeNumber(
    searchParams.get("max_selling_price"),
  );
  const priceRangeValid =
    minSellingPrice === undefined ||
    maxSellingPrice === undefined ||
    minSellingPrice <= maxSellingPrice;

  return {
    page_no: pageRaw ? parseInt(pageRaw, 10) || 1 : 1,
    limit: limitRaw ? parseInt(limitRaw, 10) || DEFAULT_LIMIT : DEFAULT_LIMIT,
    sort_by: normalizeProductListSortBy(searchParams.get("sort_by")),
    sort_type: parseSortDirectionFromSearch(searchParams),
    search: searchParams.get("search") || undefined,
    active: parseFlagFilter(searchParams, "active"),
    display: parseFlagFilter(searchParams, "display"),
    category_id: parseCategoryIdsFromSearch(searchParams.get("category_id")),
    min_selling_price: priceRangeValid ? minSellingPrice : undefined,
    max_selling_price: priceRangeValid ? maxSellingPrice : undefined,
  };
}

const ProductListing: React.FC = () => {
  const theme = useTheme();
  const { userStore } = useStores();
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const catalogProductStore = useRef(new CatalogProductStore()).current;
  const categoryStore = useRef(new CategoryStore()).current;

  const canEdit = userStore.hasPermission(EmployeeRoles.EDIT_PRODUCT);

  const initialParams = getParamsFromSearch(location.search);
  const [searchTerm, setSearchTerm] = useState(initialParams.search || "");
  const debouncedSearchTerm = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);
  const [categorySearch, setCategorySearch] = useState("");
  const debouncedCategorySearch = useDebounce(
    categorySearch,
    SEARCH_DEBOUNCE_MS,
  );
  const [minSellingPriceInput, setMinSellingPriceInput] = useState(
    initialParams.min_selling_price != null
      ? String(initialParams.min_selling_price)
      : "",
  );
  const [maxSellingPriceInput, setMaxSellingPriceInput] = useState(
    initialParams.max_selling_price != null
      ? String(initialParams.max_selling_price)
      : "",
  );
  const debouncedMinSellingPrice = useDebounce(
    minSellingPriceInput,
    SEARCH_DEBOUNCE_MS,
  );
  const debouncedMaxSellingPrice = useDebounce(
    maxSellingPriceInput,
    SEARCH_DEBOUNCE_MS,
  );

  const listing = useLocalObservable(() => ({
    params: initialParams,
    isFiltersExpanded: true,
    isLoading: false,
    deletePopupOpen: false,
    restorePopupOpen: false,
    actionProduct: null as ProductListApi | null,
    actionInProgress: false,
    categoryList: [] as FilterValue[],
    selectedCategories: (initialParams.category_id ?? []).map((id) => ({
      id: String(id),
      value: `Category ${id}`,
    })) as FilterValue[],
    setParams(updater: (prev: ProductListFilters) => ProductListFilters) {
      this.params = updater(this.params);
    },
    syncSelectedCategoriesFromParams() {
      const ids = this.params.category_id ?? [];
      this.selectedCategories = ids.map((id) => {
        const key = String(id);
        return (
          this.selectedCategories.find((item) => item.id === key) ||
          this.categoryList.find((item) => item.id === key) || {
            id: key,
            value: `Category ${id}`,
          }
        );
      });
    },
    syncFromLocationSearch(search: string) {
      const next = getParamsFromSearch(search);
      const prev = this.params;
      if (
        prev.page_no === next.page_no &&
        (prev.limit ?? DEFAULT_LIMIT) === (next.limit ?? DEFAULT_LIMIT) &&
        normalizeProductListSortBy(prev.sort_by) === next.sort_by &&
        (prev.sort_type ?? SortType.DESC) ===
          (next.sort_type ?? SortType.DESC) &&
        (prev.search ?? "") === (next.search ?? "") &&
        prev.active === next.active &&
        prev.display === next.display &&
        sameCategoryIds(prev.category_id, next.category_id) &&
        prev.min_selling_price === next.min_selling_price &&
        prev.max_selling_price === next.max_selling_price
      ) {
        return null;
      }
      this.params = next;
      this.syncSelectedCategoriesFromParams();
      return next.search ?? "";
    },
  }));

  useEffect(() => {
    const nextSearch = debouncedSearchTerm.trim() || undefined;
    if ((listing.params.search ?? "") === (nextSearch ?? "")) return;
    listing.setParams((prev) => ({
      ...prev,
      search: nextSearch,
      page_no: 1,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  useEffect(() => {
    const nextMin = parseOptionalNonNegativeNumber(debouncedMinSellingPrice);
    const nextMax = parseOptionalNonNegativeNumber(debouncedMaxSellingPrice);

    if (nextMin !== undefined && nextMax !== undefined && nextMin > nextMax) {
      return;
    }

    if (
      listing.params.min_selling_price === nextMin &&
      listing.params.max_selling_price === nextMax
    ) {
      return;
    }

    listing.setParams((prev) => ({
      ...prev,
      min_selling_price: nextMin,
      max_selling_price: nextMax,
      page_no: 1,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedMinSellingPrice, debouncedMaxSellingPrice]);

  useEffect(() => {
    let cancelled = false;
    categoryStore
      .listCategories({
        page_no: 1,
        limit: 50,
        search: debouncedCategorySearch.trim() || undefined,
        active: true,
        sort_by: "name",
        sort_type: SortType.ASC,
      })
      .then(() => {
        if (cancelled) return;
        listing.categoryList = categoryStore.categories.map((c) => ({
          id: String(c.id),
          value: c.name,
        }));
        listing.syncSelectedCategoriesFromParams();
      })
      .catch(() => {
        /* ignore filter load errors */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCategorySearch]);

  const refreshListing = () => {
    catalogProductStore.listProducts(listing.params).catch((err: unknown) => {
      showError("Error", getApiErrorMessage(err, "Failed to load products."));
    });
  };

  const textFilterSignature = [
    listing.params.search ?? "",
    listing.params.min_selling_price ?? "",
    listing.params.max_selling_price ?? "",
  ].join("|");
  const dropdownFilterSignature = [
    listing.params.active ?? "",
    listing.params.display ?? "",
    (listing.params.category_id ?? []).join(","),
  ].join("|");
  const textFilterSignatureRef = useRef(textFilterSignature);
  const dropdownFilterSignatureRef = useRef(dropdownFilterSignature);

  useEffect(() => {
    let cancelled = false;
    const textFiltersChanged =
      textFilterSignatureRef.current !== textFilterSignature;
    const dropdownFiltersChanged =
      dropdownFilterSignatureRef.current !== dropdownFilterSignature;
    textFilterSignatureRef.current = textFilterSignature;
    dropdownFilterSignatureRef.current = dropdownFilterSignature;

    // Search/price are already debounced before params update → fetch immediately.
    // Dropdown filter changes are coalesced so rapid toggles don't spam the API.
    // Pagination/sort stay immediate.
    const delay =
      textFiltersChanged || !dropdownFiltersChanged ? 0 : SEARCH_DEBOUNCE_MS;
    const requestParams: ProductListFilters = { ...listing.params };

    listing.isLoading = true;
    const timer = window.setTimeout(() => {
      catalogProductStore
        .listProducts(requestParams)
        .catch((err: unknown) => {
          if (!cancelled) {
            showError(
              "Error",
              getApiErrorMessage(err, "Failed to load products."),
            );
          }
        })
        .finally(() => {
          if (!cancelled) {
            listing.isLoading = false;
          }
        });
    }, delay);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    listing.params.page_no,
    listing.params.limit,
    listing.params.sort_by,
    listing.params.sort_type,
    listing.params.search,
    listing.params.active,
    listing.params.display,
    listing.params.category_id,
    listing.params.min_selling_price,
    listing.params.max_selling_price,
    textFilterSignature,
    dropdownFilterSignature,
    catalogProductStore,
  ]);

  useLayoutEffect(() => {
    const q = new URLSearchParams();
    q.set("page", String(listing.params.page_no));
    if (listing.params.limit) q.set("limit", String(listing.params.limit));
    q.set("sort_by", normalizeProductListSortBy(listing.params.sort_by));
    q.set(
      "sort_direction",
      listing.params.sort_type === SortType.ASC ? "ASC" : "DESC",
    );
    if (listing.params.search) q.set("search", listing.params.search);
    if (listing.params.active !== undefined)
      q.set("active", String(listing.params.active));
    if (listing.params.display !== undefined)
      q.set("display", String(listing.params.display));
    if (listing.params.category_id != null && listing.params.category_id.length > 0)
      q.set("category_id", listing.params.category_id.join(","));
    if (listing.params.min_selling_price != null) {
      q.set("min_selling_price", String(listing.params.min_selling_price));
    }
    if (listing.params.max_selling_price != null) {
      q.set("max_selling_price", String(listing.params.max_selling_price));
    }
    navigate({ search: q.toString() }, { replace: true });
  }, [listing.params, navigate]);

  useEffect(() => {
    const syncedSearch = listing.syncFromLocationSearch(location.search);
    if (
      syncedSearch !== null &&
      debouncedSearchTerm === searchTerm &&
      syncedSearch !== searchTerm
    ) {
      setSearchTerm(syncedSearch);
    }
    if (syncedSearch !== null) {
      const next = getParamsFromSearch(location.search);
      setMinSellingPriceInput(
        next.min_selling_price != null ? String(next.min_selling_price) : "",
      );
      setMaxSellingPriceInput(
        next.max_selling_price != null ? String(next.max_selling_price) : "",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const applySearchNow = (raw: string) => {
    const trimmed = raw.trim();
    setSearchTerm(raw);
    listing.setParams((prev) => ({
      ...prev,
      search: trimmed || undefined,
      page_no: 1,
    }));
  };

  const handleSearchSubmit = () => {
    applySearchNow(searchTerm);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearchSubmit();
  };

  const applyPriceFilters = () => {
    const nextMin = parseOptionalNonNegativeNumber(minSellingPriceInput);
    const nextMax = parseOptionalNonNegativeNumber(maxSellingPriceInput);

    if (nextMin !== undefined && nextMax !== undefined && nextMin > nextMax) {
      showError(
        "Invalid price range",
        "Min selling price must be less than or equal to max selling price.",
      );
      return false;
    }

    listing.setParams((prev) => ({
      ...prev,
      min_selling_price: nextMin,
      max_selling_price: nextMax,
      page_no: 1,
    }));
    return true;
  };

  const resetFilters = () => {
    setSearchTerm("");
    setCategorySearch("");
    setMinSellingPriceInput("");
    setMaxSellingPriceInput("");
    listing.params = {
      page_no: 1,
      limit: DEFAULT_LIMIT,
      sort_by: "id",
      sort_type: SortType.DESC,
      search: undefined,
      active: undefined,
      display: undefined,
      category_id: undefined,
      min_selling_price: undefined,
      max_selling_price: undefined,
    };
    listing.selectedCategories = [];
  };

  const handleApplyFilters = () => {
    applySearchNow(searchTerm);
    applyPriceFilters();
  };

  const ensureCanEdit = (actionLabel: string) => {
    if (canEdit) return true;
    showError(
      "Permission denied",
      `You don't have permission to ${actionLabel} products.`,
    );
    return false;
  };

  const handleToggleDisplay = async (product: ProductListApi) => {
    if (!ensureCanEdit("update")) return;
    const prev = product.display;
    catalogProductStore.patchListItem(product.id, { display: !prev });
    try {
      const result = await catalogProductStore.toggleDisplay(product.id);
      catalogProductStore.patchListItem(product.id, {
        display: result.display,
      });
      showSuccess(
        result.display ? "Product is now displayed." : "Product is now hidden.",
      );
    } catch (error) {
      catalogProductStore.patchListItem(product.id, { display: prev });
      showError(
        "Error",
        getApiErrorMessage(error, "Could not update display status."),
      );
    }
  };

  const handleDeleteConfirm = async () => {
    if (!ensureCanEdit("deactivate")) return;
    if (!listing.actionProduct || listing.actionInProgress) return;
    try {
      listing.actionInProgress = true;
      await catalogProductStore.toggleActive(listing.actionProduct.id);
      listing.deletePopupOpen = false;
      listing.actionProduct = null;
      showSuccess("Product deactivated.");
      refreshListing();
    } catch (error) {
      showError(
        "Error",
        getApiErrorMessage(error, "Failed to deactivate product."),
      );
    } finally {
      listing.actionInProgress = false;
    }
  };

  const handleRestoreConfirm = async () => {
    if (!ensureCanEdit("restore")) return;
    if (!listing.actionProduct || listing.actionInProgress) return;
    try {
      listing.actionInProgress = true;
      await catalogProductStore.toggleActive(listing.actionProduct.id);
      listing.restorePopupOpen = false;
      listing.actionProduct = null;
      showSuccess("Product restored.");
      refreshListing();
    } catch (error) {
      showError(
        "Error",
        getApiErrorMessage(error, "Failed to restore product."),
      );
    } finally {
      listing.actionInProgress = false;
    }
  };

  return (
    <Grid2
      size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
      sx={{ px: 2, width: "100%", maxWidth: "100%", minWidth: 0 }}
    >
      <Paper
        sx={{
          mt: 3,
          p: { xs: 2, md: 3 },
          borderRadius: 2,
          boxShadow:
            "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
          width: "100%",
          maxWidth: "100%",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            <CustomTypography variant="heading3">Products</CustomTypography>
            <CustomTypography
              variant="body2"
              color={theme.palette.customColors?.greyTextColor}
            >
              Browse and manage catalog products
            </CustomTypography>
          </Box>
          {canEdit ? (
            <AppButton
              variant="primary"
              label="Add Product"
              onClick={() => navigate("/products/new")}
            />
          ) : null}
        </Box>

        <Paper
          variant="outlined"
          sx={{
            mt: 2,
            borderRadius: 2,
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <Box
            onClick={() =>
              (listing.isFiltersExpanded = !listing.isFiltersExpanded)
            }
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              py: 0.5,
              cursor: "pointer",
              borderBottom: listing.isFiltersExpanded ? "1px solid" : "none",
              borderColor: "divider",
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <CustomTypography variant="heading6">Search</CustomTypography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                listing.isFiltersExpanded = !listing.isFiltersExpanded;
              }}
            >
              {listing.isFiltersExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Collapse in={listing.isFiltersExpanded} timeout="auto" unmountOnExit>
            <Grid2 container spacing={2} columns={12} padding={2}>
              <Grid2 size={{ xs: 12, sm: 9, md: 11, lg: 11 }}>
                <CustomTextField
                  width="100%"
                  placeholder="Search by name, SKU, or ID"
                  value={searchTerm}
                  onSearch={(value) => {
                    setSearchTerm(value || "");
                  }}
                  onChange={(e) => {
                    setSearchTerm(e.target.value || "");
                  }}
                  endAdornment={
                    <SearchIcon
                      onClick={() => handleSearchSubmit()}
                      sx={{ color: "#7C7A7F", fontSize: "20px" }}
                    />
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleKeyDown(e);
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 3, md: 1, lg: 1 }}>
                <AppButton
                  variant="primary"
                  label="Search"
                  onClick={handleSearchSubmit}
                  customStyles={{ width: "100%", height: 40 }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <AppSearchDropdownMulti
                  title="Category"
                  isMultiSelect
                  local
                  list={[
                    ...listing.selectedCategories.filter(
                      (selected) =>
                        !listing.categoryList.some(
                          (option) => option.id === selected.id,
                        ),
                    ),
                    ...listing.categoryList,
                  ]}
                  value={listing.selectedCategories}
                  handleSearch={(term) => setCategorySearch(term || "")}
                  handleSelect={(items) => {
                    listing.selectedCategories = items;
                    const nextIds = items
                      .map((item) => parseInt(String(item.id), 10))
                      .filter((id) => Number.isFinite(id) && id > 0);
                    listing.setParams((p) => ({
                      ...p,
                      category_id: nextIds.length ? nextIds : undefined,
                      page_no: 1,
                    }));
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <AppSearchDropdownMulti
                  title="Sort by"
                  isMultiSelect={false}
                  local
                  list={SORT_BY_LIST}
                  value={SORT_BY_LIST.filter(
                    (o) =>
                      o.id ===
                      normalizeProductListSortBy(listing.params.sort_by),
                  )}
                  handleSelect={(items) => {
                    const next = items[0]?.id;
                    if (!next) return;
                    listing.setParams((p) => ({
                      ...p,
                      sort_by: normalizeProductListSortBy(String(next)),
                      page_no: 1,
                    }));
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <AppSearchDropdownMulti
                  title="Direction"
                  isMultiSelect={false}
                  local
                  list={SORT_DIRECTION_LIST}
                  value={SORT_DIRECTION_LIST.filter(
                    (o) => o.id === (listing.params.sort_type ?? SortType.DESC),
                  )}
                  handleSelect={(items) => {
                    const next = items[0]?.id;
                    if (!next) return;
                    listing.setParams((p) => ({
                      ...p,
                      sort_type:
                        next === SortType.ASC ? SortType.ASC : SortType.DESC,
                      page_no: 1,
                    }));
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <AppSearchDropdownMulti
                  title="Active"
                  isMultiSelect={false}
                  local
                  list={ACTIVE_FILTER_LIST}
                  value={ACTIVE_FILTER_LIST.filter((o) => {
                    if (listing.params.active === undefined) {
                      return o.id === "all";
                    }
                    return o.id === String(listing.params.active);
                  })}
                  handleSelect={(items) => {
                    const next = items[0]?.id ?? "all";
                    listing.setParams((p) => ({
                      ...p,
                      active: next === "all" ? undefined : next === "1" ? 1 : 0,
                      page_no: 1,
                    }));
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <AppSearchDropdownMulti
                  title="Display"
                  isMultiSelect={false}
                  local
                  list={DISPLAY_FILTER_LIST}
                  value={DISPLAY_FILTER_LIST.filter((o) => {
                    if (listing.params.display === undefined) {
                      return o.id === "all";
                    }
                    return o.id === String(listing.params.display);
                  })}
                  handleSelect={(items) => {
                    const next = items[0]?.id ?? "all";
                    listing.setParams((p) => ({
                      ...p,
                      display:
                        next === "all" ? undefined : next === "1" ? 1 : 0,
                      page_no: 1,
                    }));
                  }}
                />
              </Grid2>
              <Grid2
                size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                display="flex"
                gap={2}
              >
                <Grid2 size={{ xs: 6 }}>
                  <CustomTextField
                    width="100%"
                    type="number"
                    placeholder="Min selling price"
                    value={minSellingPriceInput}
                    allowNegativeValue={false}
                    onChange={(e) => {
                      setMinSellingPriceInput(e.target.value || "");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        applySearchNow(searchTerm);
                        applyPriceFilters();
                      }
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 6 }}>
                  <CustomTextField
                    width="100%"
                    type="number"
                    placeholder="Max selling price"
                    value={maxSellingPriceInput}
                    allowNegativeValue={false}
                    onChange={(e) => {
                      setMaxSellingPriceInput(e.target.value || "");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        applySearchNow(searchTerm);
                        applyPriceFilters();
                      }
                    }}
                  />
                </Grid2>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}></Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <AppButton
                    variant="transparentPrimary"
                    label="Clear"
                    onClick={resetFilters}
                    customStyles={{ width: "100%" }}
                  />
                  <AppButton
                    variant="primary"
                    label="Apply"
                    onClick={handleApplyFilters}
                    customStyles={{ width: "100%" }}
                  />
                </Box>
              </Grid2>
            </Grid2>
          </Collapse>
        </Paper>

        {listing.isLoading ? (
          <Container
            sx={{
              minHeight: "40vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Container>
        ) : (
          <Box mt={3}>
            {catalogProductStore.products.length === 0 ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="10rem"
                textAlign="center"
                border="1px dashed"
                borderColor="divider"
                borderRadius={2}
              >
                <CustomTypography
                  variant="heading6"
                  fontWeight={500}
                  color={theme.palette.customColors?.greyTextColor}
                >
                  No products match the current search criteria.
                </CustomTypography>
              </Box>
            ) : (
              <CatalogProductTable
                maxHeight={
                  typeof window !== "undefined" ? window.innerHeight - 350 : 480
                }
                products={catalogProductStore.products}
                limit={listing.params.limit ?? DEFAULT_LIMIT}
                page_no={listing.params.page_no}
                total_count={catalogProductStore.total_count}
                sortBy={normalizeProductListSortBy(listing.params.sort_by)}
                sortType={listing.params.sort_type ?? SortType.DESC}
                onPageChange={(_e, page) =>
                  listing.setParams((p) => ({ ...p, page_no: page }))
                }
                onRowsPerPageChange={(limit) =>
                  listing.setParams((p) => ({
                    ...p,
                    limit,
                    page_no: 1,
                  }))
                }
                onSortChange={(column, sortType) =>
                  listing.setParams((p) => ({
                    ...p,
                    sort_by: column,
                    sort_type: sortType,
                    page_no: 1,
                  }))
                }
                onToggleDisplay={handleToggleDisplay}
                onDelete={(product) => {
                  if (!ensureCanEdit("deactivate")) return;
                  listing.actionProduct = product;
                  listing.deletePopupOpen = true;
                }}
                onRestore={(product) => {
                  if (!ensureCanEdit("restore")) return;
                  listing.actionProduct = product;
                  listing.restorePopupOpen = true;
                }}
              />
            )}
          </Box>
        )}
      </Paper>

      {listing.deletePopupOpen && listing.actionProduct ? (
        <ConfirmationPopup
          id={listing.actionProduct.id}
          open={listing.deletePopupOpen}
          title="Deactivate Product"
          content={`Soft-delete "${listing.actionProduct.name}"? You can restore it later from the listing.`}
          onConfirm={() => {
            void handleDeleteConfirm();
          }}
          onCancel={() => {
            listing.deletePopupOpen = false;
            listing.actionProduct = null;
          }}
        />
      ) : null}

      {listing.restorePopupOpen && listing.actionProduct ? (
        <ConfirmationPopup
          id={listing.actionProduct.id}
          open={listing.restorePopupOpen}
          title="Restore Product"
          content={`Restore "${listing.actionProduct.name}" and mark it active again?`}
          onConfirm={() => {
            void handleRestoreConfirm();
          }}
          onCancel={() => {
            listing.restorePopupOpen = false;
            listing.actionProduct = null;
          }}
        />
      ) : null}
    </Grid2>
  );
};

export default observer(ProductListing);
